import { FC, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { CpiTrip, EmissionSource, formatNumber } from './types';
import './CpiTripsTable.scss';

interface CpiTripsTableProps {
  trips: CpiTrip[];
  isLoading: boolean;
  // Server-side pagination props
  totalCount: number;
  currentPage: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  onItemsPerPageChange: (itemsPerPage: number) => void;
  // Export props
  onExport: () => void;
  isExporting: boolean;
  onExportCsv: () => void;
  isExportingCsv: boolean;
}

type SortField = keyof CpiTrip;
type SortDirection = 'asc' | 'desc' | null;

interface SortState {
  field: SortField | null;
  direction: SortDirection;
}

const ITEMS_PER_PAGE_OPTIONS = [10, 25, 50, 100, 1000];

const CpiTripsTable: FC<CpiTripsTableProps> = ({
  trips,
  isLoading,
  totalCount,
  currentPage,
  itemsPerPage,
  onPageChange,
  onItemsPerPageChange,
  onExport,
  isExporting,
  onExportCsv,
  isExportingCsv,
}) => {
  const { t } = useTranslation();

  // Local state for sorting (client-side sorting of current page)
  const [sortState, setSortState] = useState<SortState>({ field: null, direction: null });

  // Sort trips (client-side sorting of current page only)
  const sortedTrips = useMemo(() => {
    const { field, direction } = sortState;
    if (!field || !direction) return trips;

    return [...trips].sort((a, b) => {
      const aVal = a[field];
      const bVal = b[field];

      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;

      let comparison = 0;
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        comparison = aVal.localeCompare(bVal);
      } else if (typeof aVal === 'number' && typeof bVal === 'number') {
        comparison = aVal - bVal;
      }

      return direction === 'asc' ? comparison : -comparison;
    });
  }, [trips, sortState]);

  // Calculate total pages from server total count (prevent division by zero)
  const totalPages = Math.ceil(totalCount / Math.max(itemsPerPage, 1));

  // Handlers
  const handleSort = (field: SortField) => {
    setSortState((prev) => {
      if (prev.field !== field) {
        return { field, direction: 'asc' };
      }
      if (prev.direction === 'asc') {
        return { field, direction: 'desc' };
      }
      return { field: null, direction: null };
    });
  };

  const handlePageChange = (page: number) => {
    const validPage = Math.max(1, Math.min(page, totalPages));
    onPageChange(validPage);
  };

  const handleItemsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onItemsPerPageChange(Number(e.target.value));
  };

  // Render sort icon
  const renderSortIcon = (field: SortField) => {
    if (sortState.field !== field) return <span className="sort-icon">⇅</span>;
    if (sortState.direction === 'asc') return <span className="sort-icon active">↑</span>;
    if (sortState.direction === 'desc') return <span className="sort-icon active">↓</span>;
    return <span className="sort-icon">⇅</span>;
  };

  // Render emission source badge
  const renderEmissionSourceBadge = (source: EmissionSource) => {
    const badgeClass = `emission-badge emission-badge--${source.toLowerCase()}`;
    return <span className={badgeClass}>{source}</span>;
  };

  // Format date
  const formatDate = (dateStr: string) => {
    try {
      return format(new Date(dateStr), 'dd-MM-yyyy');
    } catch {
      return dateStr;
    }
  };

  if (isLoading) {
    return (
      <div className="cpi-trips-table cpi-trips-table--loading">
        <div className="cpi-trips-table__skeleton" />
      </div>
    );
  }

  return (
    <div className="cpi-trips-table">
      {/* Header with title and export button */}
      <div className="cpi-trips-table__header">
        <h2 className="cpi-trips-table__title">{t('cpi.tripDetails')}</h2>
        <div className="cpi-trips-table__export-actions">
          <button
            type="button"
            className="flat-action"
            onClick={onExport}
            disabled={isExporting || totalCount === 0}
            aria-label={t('cpi.exportToExcelAria')}
          >
            {isExporting ? (
              <>
                <span className="cpi-trips-table__export-spinner" />
                .xlsx
              </>
            ) : (
              <>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                .xlsx
              </>
            )}
          </button>
          <button
            type="button"
            className="flat-action"
            onClick={onExportCsv}
            disabled={isExportingCsv || totalCount === 0}
            aria-label={t('cpi.exportToCsvAria')}
          >
            {isExportingCsv ? (
              <>
                <span className="cpi-trips-table__export-spinner" />
                .csv
              </>
            ) : (
              <>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                .csv
              </>
            )}
          </button>
        </div>
      </div>

      {sortState.field && totalPages > 1 && (
        <p className="cpi-trips-table__sort-note">
          {t('cpi.sortCurrentPageOnly')}
        </p>
      )}

      {/* Table */}
      <div className="cpi-trips-table__wrapper">
        <table className="cpi-trips-table__table">
          <thead>
            <tr>
              <th onClick={() => handleSort('orderNumber')}>
                {t('cpi.trip.orderNumber')} {renderSortIcon('orderNumber')}
              </th>
              <th onClick={() => handleSort('transportOrderNumber')}>
                Transportorder {renderSortIcon('transportOrderNumber')}
              </th>
              <th onClick={() => handleSort('volgnummer')}>
                {t('cpi.trip.volgnummer')} {renderSortIcon('volgnummer')}
              </th>
              <th onClick={() => handleSort('executionDate')}>
                {t('cpi.trip.date')} {renderSortIcon('executionDate')}
              </th>
              <th onClick={() => handleSort('vehicleId')}>
                {t('cpi.trip.vehicle')} {renderSortIcon('vehicleId')}
              </th>
              <th onClick={() => handleSort('carrierId')}>
                {t('cpi.trip.carrier')} {renderSortIcon('carrierId')}
              </th>
              <th onClick={() => handleSort('clientId')}>
                {t('cpi.trip.client')} {renderSortIcon('clientId')}
              </th>
              <th onClick={() => handleSort('toc')}>
                {t('cpi.trips.toc')} {renderSortIcon('toc')}
              </th>
              <th onClick={() => handleSort('weightTons')}>
                {t('cpi.trip.weight')} {renderSortIcon('weightTons')}
              </th>
              <th onClick={() => handleSort('distanceKm')}>
                {t('cpi.trip.distance')} {renderSortIcon('distanceKm')}
              </th>
              <th onClick={() => handleSort('loadPerformanceTonKm')}>
                {t('cpi.trip.loadPerformance')} {renderSortIcon('loadPerformanceTonKm')}
              </th>
              <th onClick={() => handleSort('emissionWtwKg')}>
                WTW (kg) {renderSortIcon('emissionWtwKg')}
              </th>
              <th onClick={() => handleSort('emissionWttKg')}>
                WTT (kg) {renderSortIcon('emissionWttKg')}
              </th>
              <th onClick={() => handleSort('emissionTtwKg')}>
                TTW (kg) {renderSortIcon('emissionTtwKg')}
              </th>
              <th onClick={() => handleSort('emissionWtwHubKg')} title={t("cpi.trips.hocShareTooltip")}>
                Hub WTW (kg) {renderSortIcon('emissionWtwHubKg')}
              </th>
              <th title={t("cpi.trips.combinedWtwTooltip")}>
                Combined WTW (kg)
              </th>
              <th onClick={() => handleSort('hocId')}>
                HOC {renderSortIcon('hocId')}
              </th>
              <th title={t("cpi.trips.hubWtwPerTonTooltip")}>
                HOC KPI
              </th>
              <th onClick={() => handleSort('cpiWtw')}>
                CPI Transport {renderSortIcon('cpiWtw')}
              </th>
              <th title={t("cpi.trips.combinedWtwPerTonKmTooltip")}>
                CPI Total
              </th>
              <th onClick={() => handleSort('emissionSource')}>
                {t('cpi.trip.source')} {renderSortIcon('emissionSource')}
              </th>
              {/* <th onClick={() => handleSort('indicativeCostEuros')}>
                {t('cpi.trip.cost')} {renderSortIcon('indicativeCostEuros')}
              </th> */}
            </tr>
          </thead>
          <tbody>
            {sortedTrips.length === 0 ? (
              <tr>
                <td colSpan={21} className="cpi-trips-table__empty">
                  {t('cpi.trip.noData')}
                </td>
              </tr>
            ) : (
              sortedTrips.map((trip) => (
                <tr key={`${trip.orderNumber || ''}-${trip.volgnummer ?? 0}-${trip.executionDate || ''}`}>
                  <td>{trip.orderNumber || '-'}</td>
                  <td>{trip.transportOrderNumber || '-'}</td>
                  <td>{trip.volgnummer ?? '-'}</td>
                  <td>{formatDate(trip.executionDate || '')}</td>
                  <td>{trip.vehicleId || '-'}</td>
                  <td>{trip.carrierId || '-'}</td>
                  <td>{trip.clientId || '-'}</td>
                  <td>{trip.toc || '-'}</td>
                  <td>{formatNumber(trip.weightTons, 2)}</td>
                  <td>{formatNumber(trip.distanceKm, 5)}</td>
                  <td>{formatNumber(trip.loadPerformanceTonKm, 2)}</td>
                  <td>{formatNumber(trip.emissionWtwKg, 2)}</td>
                  <td>{formatNumber(trip.emissionWttKg, 2)}</td>
                  <td>{formatNumber(trip.emissionTtwKg, 2)}</td>
                  <td>{trip.emissionWtwHubKg != null ? formatNumber(trip.emissionWtwHubKg, 2) : '-'}</td>
                  <td>{formatNumber(trip.emissionWtwKg + (trip.emissionWtwHubKg ?? 0), 2)}</td>
                  <td>{trip.hocId || '-'}</td>
                  <td>{trip.emissionWtwHubKg != null && trip.weightTons > 0 ? formatNumber(trip.emissionWtwHubKg / trip.weightTons, 4) : '-'}</td>
                  <td>{formatNumber(trip.cpiWtw, 4)}</td>
                  <td>{trip.loadPerformanceTonKm > 0 ? formatNumber((trip.emissionWtwKg + (trip.emissionWtwHubKg ?? 0)) / trip.loadPerformanceTonKm, 4) : '-'}</td>
                  <td>{trip.emissionSource ? renderEmissionSourceBadge(trip.emissionSource) : '-'}</td>
                  {/* <td>&euro; {formatNumber(trip.indicativeCostEuros, 2)}</td> */}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalCount > 0 && (
        <div className="cpi-trips-table__pagination">
          <div className="cpi-trips-table__pagination-info">
            {t('Showing')} {(currentPage - 1) * itemsPerPage + 1} {t('to')}{' '}
            {Math.min(currentPage * itemsPerPage, totalCount)} {t('of')}{' '}
            {totalCount} {t('entries')}
          </div>

          <div className="cpi-trips-table__pagination-controls">
            <select
              value={itemsPerPage}
              onChange={handleItemsPerPageChange}
              className="cpi-trips-table__page-size"
            >
              {ITEMS_PER_PAGE_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option} {t('per page')}
                </option>
              ))}
            </select>

            <div className="cpi-trips-table__page-nav">
              <button
                type="button"
                onClick={() => handlePageChange(1)}
                disabled={currentPage <= 1}
                className="cpi-trips-table__page-btn"
                aria-label={t("First page")}
              >
                ««
              </button>
              <button
                type="button"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage <= 1}
                className="cpi-trips-table__page-btn"
                aria-label={t("Previous page")}
              >
                «
              </button>
              <span className="cpi-trips-table__page-current">
                {currentPage} / {totalPages || 1}
              </span>
              <button
                type="button"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage >= totalPages}
                className="cpi-trips-table__page-btn"
                aria-label={t("Next page")}
              >
                »
              </button>
              <button
                type="button"
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage >= totalPages}
                className="cpi-trips-table__page-btn"
                aria-label={t("Last page")}
              >
                »»
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CpiTripsTable;
